
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { omit } from 'lodash';
import ConfirmationOverlay from '@/components/ConfirmationOverlay.vue';
import ConfirmationEventPayload from '@/types/ConfirmationEventPayload';
import FbaApiError from '@/types/FbaApiError';
import globalConstants from '@/shared/globalConstants';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import { isNullOrUndefined } from '@/shared/helpers';
import { DateTime } from 'luxon';
import { AssociationType } from '@/types/AssociationType';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import {
    TrailerAssessmentAnswer,
    TrailerAssessmentQuestion,
} from '@/types/TrailerAssessmentQuestion';
import { TrailerAssessmentOutcome } from '@/types/TrailerAssessmentOutcome';
import checkTrailerAvailability from '@/shared/mixins/checkTrailerAvailability';

export default defineComponent({
    components: {
        ConfirmationOverlay,
        SomethingWrongOverlay,
    },
    data(): {
        company: string;
        popupStage: number;
        acceptedFirstStage: boolean;
        trailerNumber: string;
        damageConfirmed: boolean;
        signatureRecordedTimestamp: string | null;
        submitting: boolean;
        showLoading: 'accept' | 'reject' | null;
    } {
        return {
            company: '',
            popupStage: 1,
            acceptedFirstStage: true,
            trailerNumber: '',
            damageConfirmed: false,
            signatureRecordedTimestamp: null,
            submitting: false,
            showLoading: null,
        };
    },
    props: {
        association: {
            type: String as PropType<AssociationType>,
            required: true,
        },
        skipDotPrompt: {
            type: Boolean,
            default: false,
        },
        isDotSafe: {
            type: Boolean,
            default: false,
        },
    },
    created(): void {
        this.trailerNumber = this.$route.params.trailerNumber as string;
        this.company = this.$route.params.company as string;
    },
    computed: {
        ...mapGetters({
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
            selectedTrailer: 'selectedTrailer',
            trailerSelectionMethod: 'getTrailerSelectionMethod',
        }),
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        hasError() {
            return !isNullOrUndefined(this?.error?.message);
        },
        assistanceCompany(): string {
            return this.company === 'knight'
                ? globalConstants.trailer.assistance.knight
                : globalConstants.trailer.assistance.swift;
        },
        assistanceNumber(): string {
            return this.company === 'knight'
                ? process.env.VUE_APP_KNIGHT_BREAKDOWN_NUMBER
                : process.env.VUE_APP_SWIFT_ONROAD_NUMBER;
        },
        dotInspectionExpired(): boolean {
            return this.selectedTrailer?.isDotInspectionExpired === true;
        },
        takePossessionAcceptText() {
            return this.showLoading === 'accept'
                ? 'Submitting'
                : 'Confirmed, I will take possession';
        },
        takePossessionRejectText() {
            return this.showLoading === 'reject' ? 'Submitting' : 'Decline';
        },
        takePossessionAndContactAcceptText() {
            return this.showLoading === 'accept'
                ? 'Submitting'
                : 'Yes, I will take possession and contact.';
        },
        takePossessionAndContactRejectText() {
            return this.showLoading === 'reject' ? 'Submitting' : 'Decline';
        },
    },
    mixins: [checkTrailerAvailability],
    methods: {
        confirmationShown(): boolean {
            if (this.skipDotPrompt === true) {
                this.popupStage = 2;
                this.acceptedFirstStage = this.isDotSafe;
            }

            return this.$route.query.confirmation === 'acceptwithdamage';
        },
        confirmDisclaimer() {
            this.damageConfirmed = !this.damageConfirmed;

            if (this.damageConfirmed) {
                this.signatureRecordedTimestamp = DateTime.now()
                    .toUTC()
                    .toISO();
            } else {
                this.signatureRecordedTimestamp = null;
            }
        },
        goBackToBeginningOfAssessment(): void {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.$router.push({
                name: 'TrailerAssessment',
                query: omit(this.$route.query, 'confirmation'),
            });
            this.$router.go(0);
        },
        changeStage(val: ConfirmationEventPayload): void {
            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_QUESTION,
                {
                    question: TrailerAssessmentQuestion.DOT_SAFE,
                    answer: val.didAccept
                        ? TrailerAssessmentAnswer.YES
                        : TrailerAssessmentAnswer.NO,
                }
            );

            this.popupStage = 2;
            this.acceptedFirstStage = val.didAccept;
        },
        async onAccept(val: ConfirmationEventPayload): Promise<void> {
            this.submitting = true;
            this.showLoading = 'accept';

            this.noteAcceptanceForAssessment(val.didAccept);

            let response;
            try {
                response = await this.confirmTrailer();
            } finally {
                this.submitting = false;
                this.showLoading = null;
            }

            if (response === false || isNullOrUndefined(response)) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    new FbaApiError('Trailer attach/detach association failed')
                );
                return;
            }

            this.$router.push({
                name: 'AssociateTrailerSuccess',
                query: {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    hasBodyDamage: 'true',
                },
            });
        },
        async onReject(val: ConfirmationEventPayload): Promise<void> {
            this.submitting = true;
            this.showLoading = 'reject';

            this.noteAcceptanceForAssessment(val.didAccept);

            let response;
            try {
                response = await this.postAssessment();
            } finally {
                this.submitting = false;
                this.showLoading = null;
            }

            if (response === false || isNullOrUndefined(response)) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    new FbaApiError('Trailer attach/detach association failed')
                );
                return;
            }

            this.$router.push({
                name: 'TrailerNotFit',
                query: { trailerNumber: this.trailerNumber },
            });
        },
        async postAssessment() {
            const hasPhotos = this.$store.getters.trailerPhotosCount > 0;
            const assessmentResponse = await this.$store.dispatch(
                ActionTypes.POST_TRAILER_ASSESSMENT,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    eventName: this.association,
                    damageSignatureRecordedTimestamp:
                        this.signatureRecordedTimestamp,
                    hasPhotos,
                    source: globalConstants.trailer.trailerAssessment.source,
                    entryPoint:
                        globalConstants.trailer.trailerAssessment.entryPoints
                            .attach,
                }
            );

            if (assessmentResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    assessmentResponse as FbaApiError
                );
                return false;
            }

            if (
                assessmentResponse?.didSucceed &&
                assessmentResponse?.assessmentId &&
                hasPhotos
            ) {
                await this.$store.dispatch(
                    ActionTypes.SUBMIT_TRAILER_PHOTOS,
                    assessmentResponse.assessmentId
                );
            }

            return assessmentResponse?.didSucceed;
        },
        async confirmTrailer(): Promise<boolean> {
            const trailerQrId = this.selectedTrailer?.trailerQr;
            const trailerSelectionMethod = this.trailerSelectionMethod;
            const assessmentRes = await this.postAssessment();
            if (!assessmentRes) return false;

            const available = await this.checkTrailerAvailability();

            if (available == false) {
                this.$router.push({
                    name: 'TrailerUnavailable',
                    query: {
                        trailerNumber: this.trailerNumber,
                        trailerCompany: this.company,
                    },
                });
                return false;
            }

            this.$store.commit(MutationTypes.CLEAR_SELECTED_TRAILER, undefined);

            const associationResponse = (await this.$store.dispatch(
                ActionTypes.POST_ATTACH_DRIVER_TRAILER,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    driverLocation: this.bestAvailableLocation,
                    trailerQrId,
                    trailerSelectionMethod,
                }
            )) as boolean | FbaApiError;
            if (associationResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    associationResponse as FbaApiError
                );
                return false;
            }

            return assessmentRes && associationResponse;
        },
        noteAcceptanceForAssessment(accepted: boolean): void {
            const questionAsked = this.acceptedFirstStage
                ? TrailerAssessmentQuestion.WILL_TAKE_POSSESSION
                : TrailerAssessmentQuestion.WILL_CONTACT_BREAKDOWN;

            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_QUESTION,
                {
                    question: questionAsked,
                    answer: accepted
                        ? TrailerAssessmentAnswer.YES
                        : TrailerAssessmentAnswer.NO,
                }
            );

            const outcome = accepted
                ? TrailerAssessmentOutcome.TOOK_POSSESSION
                : TrailerAssessmentOutcome.DECLINED_POSSESSION;

            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_OUTCOME,
                outcome
            );
        },
    },
});
